import {
  ChangeStatusModal,
  CTQButton,
  ExpandButton,
  ProjectHistoryButton,
  ProjectImage,
  ProjectStatusBadges,
  ProjectTeamButton,
  ProjectTitle,
  ProjectTitleInfo,
  StatusButton,
  TeamsButton,
} from "@/containers/Project/components/ProjectHeader/components";
import * as S from "@/containers/Project/components/ProjectHeader/styled";
import { PROJECT_STATUS } from "@/enums";
import { ProjectHeaderElements } from "@/enums/ProjectHeaderElements";
import { getProjectStatusTranslationId, isProjectStatus } from "@/helpers";
import { useIsProjectLeaderOnly } from "@/helpers/permissions";
import { useGetError } from "@/hooks";
import { useProjectContext } from "@/providers";
import {
    useGetAdminFlatFormsQuery,
    useGetStatusesQuery,
} from "@/services";
import { Col } from "antd";
import _ from "lodash";
import { arrayOf, bool, func, string } from "prop-types";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { RisksAndIssuesButton } from "../components/Buttons/RisksAndIssuesButton";

interface IProps {
  showDetails: boolean;
  handleShowDetails: () => void;
  mobile: boolean;
  source: "project" | "intake";
  inActiveElements?: ProjectHeaderElements[];
  expandButtonText?: string;
  disableExpand?: boolean;
}

interface IFlatForm {
  values: [];
}

interface IFlatFormValue {
  id: string;
  label: {
    fallback: string;
  };
}

const ProjectHeader = ({
  showDetails,
  handleShowDetails,
  mobile,
  source,
  inActiveElements = [],
  expandButtonText,
  disableExpand = false,
}: IProps) => {
  const { projectId } = useParams();
  const intl = useIntl();
  const {
    project: {
      status: projectStatus,
      projectName,
      projectNumber,
      productCategory,
      projectType,
      statusKpis,
      secureProject,
    },
    projectLoading,
    project,
  } = useProjectContext();

  const {
    refetch: refetchAvailableStatuses,
    data: statuses,
    isError,
    isLoading: loadingStatuses,
  } = useGetStatusesQuery(projectId);

  const { data: { flatForms } = {} } = useGetAdminFlatFormsQuery({});

  const [changeStatusModalOpen, setChangeStatusModalOpen] = useState(false);

  const status = {
    id: projectStatus,
    label: intl.formatMessage({
      id: getProjectStatusTranslationId(projectStatus),
    }),
  };

  const iasValue = project?.ias;

  const platformValue = project?.platformAmortization;

  let iasText = "Not Checked";
  let platformText = "Not Checked";

  flatForms?.forEach((formField: IFlatForm) => {
    formField?.values?.forEach((ffValue: IFlatFormValue) => {
      if (ffValue.id === iasValue) {
        iasText = ffValue.label.fallback;
      } else if (ffValue.id === platformValue) {
        platformText = ffValue.label.fallback;
      }
    });
  });
  const showStatusHoverMessage =
    useIsProjectLeaderOnly(projectLoading, project) &&
    (iasText === "Yes" || platformText === "Yes") &&
    projectStatus !== PROJECT_STATUS.intake.draft.id &&
    projectStatus !== PROJECT_STATUS.intake.onHold.id;
  const canChangeStatus = (): boolean => {
    let canChangeStatus = false;

    if (
      source === "project" &&
      isProjectStatus(projectStatus) &&
      !showStatusHoverMessage
    ) {
      canChangeStatus = true;
    } else if (
      source === "intake" &&
      !isProjectStatus(projectStatus) &&
      !showStatusHoverMessage
    ) {
      canChangeStatus = true;
    }

    return canChangeStatus;
  };

  useGetError({ isError, translationId: "projects.projectStatuses" });

  function openChangeStatusModal() {
    refetchAvailableStatuses();
    setChangeStatusModalOpen(true);
  }

  return (
    <>
      <S.ProjectHeaderRow justify="start">
        {!inActiveElements.includes(ProjectHeaderElements.IMAGE) && (
          <S.ImageCol flex="95px">
            <ProjectImage projectId={projectId} projectName={projectName} />
          </S.ImageCol>
        )}
        {!inActiveElements.includes(ProjectHeaderElements.TITLE) && (
          <S.TitleCol flex={12} $mobile={mobile}>
            <ProjectTitle
              projectName={projectName}
              projectNumber={projectNumber}
              isSecure={secureProject}
            />
            <ProjectTitleInfo
              productCategory={productCategory}
              projectType={projectType}
            />
          </S.TitleCol>
        )}
        {!inActiveElements.includes(ProjectHeaderElements.STATUS) && (
          <Col flex="min-content">
            <StatusButton
              data-test-id="status-button"
              status={status}
              handleChangeStatus={openChangeStatusModal}
              disabled={!canChangeStatus() || _.isEmpty(statuses)}
              showStatusHoverMessage={showStatusHoverMessage}
            />
          </Col>
        )}
        {!inActiveElements.includes(ProjectHeaderElements.KPIS) && (
          <S.ActionCol flex={4} $mobile={mobile}>
            {statusKpis && <ProjectStatusBadges statusKpis={statusKpis} />}
          </S.ActionCol>
        )}
        {!inActiveElements.includes(ProjectHeaderElements.RISKS_AND_ISSUES) &&
        // Feature flag: remove in future
        window?.localStorage?.getItem('showRisksAndIssuesButton') === 'true' && (
          <S.ActionCol flex="min-content">
            <RisksAndIssuesButton
              data-test-id="risks-and-issues-button"
              projectId={projectId}
            />
          </S.ActionCol>
        )}
        {!inActiveElements.includes(ProjectHeaderElements.CTQ) && (
          <S.ActionCol flex="min-content">
            <CTQButton
              data-test-id="ctq-button"
              projectNumber={projectNumber}
            />
          </S.ActionCol>
        )}
        {!inActiveElements.includes(ProjectHeaderElements.TEAMS) && (
          <S.ActionCol flex="min-content">
            <TeamsButton data-test-id="teams-button" />
          </S.ActionCol>
        )}
        {!inActiveElements.includes(ProjectHeaderElements.PROJECT_TEAM) && (
          <S.ActionCol flex="min-content">
            <ProjectTeamButton data-test-id="project-team-button" />
          </S.ActionCol>
        )}
        {!inActiveElements.includes(ProjectHeaderElements.HISTORY) && (
          <S.ActionCol flex="min-content">
            <ProjectHistoryButton
              data-test-id="project-history-button"
              projectId={projectId}
            />
          </S.ActionCol>
        )}
        {!inActiveElements.includes(ProjectHeaderElements.EXPAND) && (
          <S.ActionCol flex="min-content">
            <ExpandButton
              data-test-id="drawer-expand-button"
              handleShowDetails={handleShowDetails}
              showDetails={showDetails}
              expandButtonText={expandButtonText ?? ""}
              disableExpand={disableExpand}
            />
          </S.ActionCol>
        )}
      </S.ProjectHeaderRow>
      <ChangeStatusModal
        isOpen={changeStatusModalOpen}
        onCancel={(): void => setChangeStatusModalOpen(false)}
        availableStatuses={statuses}
        loadingStatuses={loadingStatuses}
      />
    </>
  );
};

ProjectHeader.propTypes = {
  showDetails: bool,
  handleShowDetails: func,
  mobile: bool,
  inActiveElements: arrayOf(string),
  expandButtonText: string,
};

export { ProjectHeader };